import { useEffect, useState } from "react"
import styled from "styled-components"
import AdminService from "../../../services/admin.service"

interface Rule {
    id: number; tour_id?: string; titulo?: string; regla_texto: string;
    prioridad?: number; activa: boolean; creado_por?: string; creado_en?: string;
}

export default function Reglas() {
    const [rules, setRules] = useState<Rule[]>([])
    const [loading, setLoading] = useState(false)
    const [titulo, setTitulo] = useState("")
    const [texto, setTexto] = useState("")
    const [tourId, setTourId] = useState("")
    const [saving, setSaving] = useState(false)
    // Edicion en linea de una regla a la vez. El backend (PUT /rules/:id) ya
    // aceptaba titulo y regla_texto con COALESCE; faltaba solo la UI.
    const [editId, setEditId] = useState<number | null>(null)
    const [editTitulo, setEditTitulo] = useState("")
    const [editTexto, setEditTexto] = useState("")
    const [editSaving, setEditSaving] = useState(false)

    const load = async () => {
        setLoading(true)
        try { const { data } = await AdminService.getBotRules(); setRules(Array.isArray(data) ? data : []) }
        catch { setRules([]) } finally { setLoading(false) }
    }
    useEffect(() => { load() }, [])

    const crear = async () => {
        if (!texto.trim()) return
        setSaving(true)
        try {
            await AdminService.createBotRule({ regla_texto: texto.trim(), titulo: titulo.trim() || undefined, tour_id: tourId.trim() || undefined })
            setTitulo(""); setTexto(""); setTourId(""); await load()
        } catch {} finally { setSaving(false) }
    }
    const toggle = async (r: Rule) => {
        try { await AdminService.updateBotRule(r.id, { activa: !r.activa }); await load() } catch {}
    }
    const editar = (r: Rule) => { setEditId(r.id); setEditTitulo(r.titulo || ""); setEditTexto(r.regla_texto) }
    const cancelar = () => { setEditId(null); setEditTitulo(""); setEditTexto("") }
    const guardar = async () => {
        if (editId === null || !editTexto.trim()) return
        setEditSaving(true)
        try {
            // titulo vacio se manda como "" (no null): con COALESCE, null
            // dejaria el titulo anterior y no habria forma de borrarlo.
            await AdminService.updateBotRule(editId, { titulo: editTitulo.trim(), regla_texto: editTexto.trim() })
            cancelar(); await load()
        } catch {} finally { setEditSaving(false) }
    }

    return (
        <Wrap>
            <NewRule>
                <h3>Enseñar una regla al bot</h3>
                <input placeholder="Título (opcional)" value={titulo} onChange={e => setTitulo(e.target.value)} />
                <textarea placeholder="Escribí la regla en lenguaje natural. Ej: Ofrecer el Géiser del Tatio en los últimos días del itinerario, no en los primeros." value={texto} onChange={e => setTexto(e.target.value)} />
                <div className="row">
                    <input placeholder="tour_id (opcional, vacío = regla general)" value={tourId} onChange={e => setTourId(e.target.value)} />
                    <button disabled={saving || !texto.trim()} onClick={crear}>{saving ? "Guardando…" : "Agregar regla"}</button>
                </div>
            </NewRule>
            <Head><span>{rules.length} reglas</span><button onClick={load}>&#8635;</button></Head>
            {loading && <Empty>Cargando…</Empty>}
            {!loading && rules.length === 0 && <Empty>Sin reglas todavía</Empty>}
            {rules.map(r => editId === r.id ? (
                <RuleCard key={r.id} className="editing">
                    <div className="body edit">
                        <input placeholder="Título (opcional)" value={editTitulo} onChange={e => setEditTitulo(e.target.value)} />
                        <textarea value={editTexto} onChange={e => setEditTexto(e.target.value)} />
                        <span className="meta">{r.tour_id ? `tour: ${r.tour_id}` : "regla general"}{r.creado_por ? ` · ${r.creado_por}` : ""}</span>
                    </div>
                    <div className="actions">
                        <button className="save" disabled={editSaving || !editTexto.trim()} onClick={guardar}>{editSaving ? "Guardando…" : "Guardar"}</button>
                        <button className="cancel" disabled={editSaving} onClick={cancelar}>Cancelar</button>
                    </div>
                </RuleCard>
            ) : (
                <RuleCard key={r.id} className={r.activa ? "" : "off"}>
                    <div className="body">
                        {r.titulo && <strong>{r.titulo}</strong>}
                        <p>{r.regla_texto}</p>
                        <span className="meta">{r.tour_id ? `tour: ${r.tour_id}` : "regla general"}{r.creado_por ? ` · ${r.creado_por}` : ""}</span>
                    </div>
                    <div className="actions">
                        <button className={r.activa ? "on" : "offbtn"} onClick={() => toggle(r)}>{r.activa ? "Activa" : "Inactiva"}</button>
                        <button className="edit" disabled={editId !== null} onClick={() => editar(r)}>Editar</button>
                    </div>
                </RuleCard>
            ))}
        </Wrap>
    )
}

const Wrap = styled.div`height:100%;overflow-y:auto;display:flex;flex-direction:column;gap:10px;`
const NewRule = styled.div`
border:1px solid #e3ddd2;border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:8px;background:#faf7f1;
h3{margin:0 0 4px;font-size:15px;color:#2D2318;}
input,textarea{border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-family:inherit;font-size:13px;box-sizing:border-box;width:100%;}
textarea{min-height:60px;resize:vertical;}
.row{display:flex;gap:8px;}
.row input{flex:1;}
.row button{border:none;border-radius:8px;padding:9px 16px;background:#C4622D;color:#fff;cursor:pointer;font-size:13px;white-space:nowrap;}
.row button:disabled{opacity:.5;cursor:default;}
`
const Head = styled.div`
display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#8B7355;margin-top:4px;
button{border:none;background:#f2ebe0;border-radius:8px;width:28px;height:28px;cursor:pointer;}
`
const RuleCard = styled.div`
display:flex;justify-content:space-between;align-items:flex-start;gap:12px;border:1px solid #e3ddd2;border-radius:10px;padding:12px 14px;background:#fff;
&.off{opacity:.6;}
.body strong{font-size:14px;color:#2D2318;}
.body p{margin:4px 0;font-size:13px;color:#3c352b;line-height:1.5;}
.body .meta{font-size:11px;color:#A09070;}
button{border:none;border-radius:10px;padding:6px 14px;cursor:pointer;font-size:12px;flex-shrink:0;}
button.on{background:#e2efda;color:#3c6b2f;}
button.offbtn{background:#f2ebe0;color:#8B7355;}
button.edit{background:transparent;color:#8B7355;border:1px solid #e3ddd2;}
button.save{background:#C4622D;color:#fff;}
button.cancel{background:#f2ebe0;color:#8B7355;}
button:disabled{opacity:.5;cursor:default;}
.actions{display:flex;flex-direction:column;gap:6px;flex-shrink:0;}
.body{flex:1;min-width:0;}
.body.edit{display:flex;flex-direction:column;gap:6px;}
.body.edit input,.body.edit textarea{border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-family:inherit;font-size:13px;box-sizing:border-box;width:100%;}
.body.edit textarea{min-height:80px;resize:vertical;}
&.editing{border-color:#C4622D;}
`
const Empty = styled.div`color:#A09070;font-size:13px;text-align:center;padding:24px;`
